import { useCallback, useRef, useState, type ReactNode } from "react";
import { prettifyApiError } from "@/shared/api";
import { draftToOverride, getMailBackend, type MailDraft } from "@/features/mail/api";
import { MailComposeMultiDialog } from "@/features/mail/MailComposeMultiDialog";
import { sendPickupMail } from "./api";
import { formatPickupMailMessage } from "./pickupMailFeedback";
import type { Order } from "./types";

export type PickupMailAction = {
  /** Handler «Invia LDV» per un ordine. Passalo a PickupMailButton/ShippingSection. */
  sendPickupMail: (order: Order) => Promise<void>;
  /** Busy per-id (non un booleano globale): il consumer chiede `.has(order.id)`. */
  pickupMailBusyIds: ReadonlySet<string>;
  /** Popup di conferma pre-invio: il consumer lo renderizza. `null` quando chiuso. */
  dialog: ReactNode;
};

type UsePickupMailActionOpts = {
  onRefresh: () => void;
  onMessage: (msg: string | null) => void;
  onError: (msg: string | null) => void;
  isMockMode: boolean;
};

/** Estratto da OrdiniPage (bug #4, 2026-07-16): «come si manda la mail di
 *  ritiro» in UN posto solo. La pagina ordine ne aveva una seconda copia che
 *  saltava il gate `mail_backend` → su smtp (il backend REALE di om.bscom.it)
 *  la mail al fornitore partiva al click, senza rilettura. Stessa lezione dei
 *  bug invoicing (root cause 2/3/5 = la stessa regola in 5 copie): un
 *  invariante duplicato è un invariante che prima o poi diverge.
 *
 *  Gemello di `useLabelActions`: stessi sink parametrici, stesso contratto
 *  (busy per-id + dialog che il consumer renderizza). Il busy resta un Set
 *  anche per i consumer single-order — chi mostra UN ordine chiede
 *  `pickupMailBusyIds.has(order.id)` e non paga nulla, mentre la lista
 *  conserva la proprietà che le serve (vedi functional setState sotto).
 *
 *  Il dialog è `null` finché il popup non si apre: montarlo sempre romperebbe
 *  i `findByRole("dialog")` al singolare dei test switch/reship (e sarebbe
 *  comunque un nodo inutile nel DOM). */
export function usePickupMailAction(opts: UsePickupMailActionOpts): PickupMailAction {
  // Sink destrutturati, non l'oggetto ``opts`` intero (nuovo letterale ad ogni
  // render del caller): altrimenti ``handleSendPickupMail`` cambierebbe
  // identità ad ogni render di OrdiniPage → re-render a cascata su tutte le
  // OrderCard. Stesso motivo di useLabelActions.
  const { onRefresh, onMessage, onError, isMockMode } = opts;
  // Pickup-mail: stesso pattern di labelsBusyIds (parent stabile, no
  // state-leak su collapse, dedup doppio-click). Cleanup garantito da
  // finally anche se Thunderbird non parte (mostriamo l'errore).
  const [pickupMailBusyIds, setPickupMailBusyIds] = useState<Set<string>>(new Set());
  // Popup pre-invio pickup mail (Task 11, spec 2026-07-02): gatato su
  // mail_backend === "smtp" (getMailBackend), stesso pattern del toolbar
  // AS/WF. ``orderId``/``original`` servono a handlePickupComposeSend per
  // richiamare sendPickupMail con l'override del delta editato.
  const [pickupComposeState, setPickupComposeState] = useState<{
    orderId: string;
    draft: MailDraft;
    original: MailDraft;
    // Fix review CRITICAL: wiring uniforme con AS/WF (vedi OrdiniActionToolbar).
    // Il preview pickup è single-order: resta sempre vuoto, ma la prop passa
    // comunque al dialog per coerenza col contratto MailComposeMultiDialog.
    errors: { order_id: string; message: string }[];
  } | null>(null);
  const [pickupComposeSending, setPickupComposeSending] = useState(false);
  const [pickupComposeError, setPickupComposeError] = useState<string | null>(null);

  // Guard idempotenza: gli id "in volo" vivono in un ref, non dentro
  // l'updater di setState. Assegnare una variabile DENTRO l'updater e' un
  // effetto collaterale (React li vuole PURI e in StrictMode li esegue due
  // volte) e la lettura subito dopo funziona solo finche' React valuta
  // l'updater in modo eager — cosa non garantita se sull'hook c'e' gia' un
  // update in coda. Il ref e' sincrono per contratto; lo stato resta la
  // fonte del render.
  const inFlightRef = useRef<Set<string>>(new Set());

  /** Rilascia il busy di un ordine. `prev` invariato se non c'era: niente
   *  re-render inutile (il set è confrontato per identità). */
  const releaseBusy = useCallback((orderId: string) => {
    if (inFlightRef.current.has(orderId)) {
      const next = new Set(inFlightRef.current);
      next.delete(orderId);
      inFlightRef.current = next; // niente mutazione in-place
    }
    setPickupMailBusyIds((prev) => {
      if (!prev.has(orderId)) return prev;
      const next = new Set(prev);
      next.delete(orderId);
      return next;
    });
  }, []);

  const handleSendPickupMail = useCallback(
    async (order: Order) => {
      // Audit 2026-05-13 #18: stesso pattern di ``handleGenerateLabel``.
      // Il guard NON legge ``pickupMailBusyIds`` dal closure (callback
      // identity stabile, niente re-render cascata sulle OrderCard) ma il
      // ref, che e' sincrono: lo stato serve solo a disegnare il bottone.
      if (inFlightRef.current.has(order.id)) return;
      inFlightRef.current = new Set(inFlightRef.current).add(order.id);
      setPickupMailBusyIds((prev) => new Set(prev).add(order.id));
      onMessage(null);
      onError(null);
      // Il popup di conferma (ramo smtp) tiene il bottone 'busy' finche' la
      // mail non viene inviata (handlePickupComposeSend) o il popup chiuso
      // (onClose): in quel caso NON rilasciamo il busy id qui nel finally,
      // altrimenti il bottone tornerebbe cliccabile col popup ancora aperto
      // → secondo preview/popup per lo stesso ordine.
      let keepBusy = false;
      try {
        // Fallisce CHIUSO, non aperto: se /api/mail/config non risponde, il
        // ramo "thunderbird" salterebbe il popup e su om.bscom.it (che E' smtp)
        // manderebbe una mail vera al fornitore mai riletta. Sbagliando verso
        // smtp il peggio e' un popup di troppo sul desktop: si chiude.
        const backend = await getMailBackend().catch(() => "smtp" as const);
        if (backend === "smtp") {
          const preview = await sendPickupMail(order.id, { preview: true });
          if ("preview" in preview) {
            const draft = preview.drafts[0];
            // Nessuna bozza da rileggere: NON si prosegue con l'invio diretto.
            // Il backend ne costruisce sempre esattamente una, quindi qui non
            // si arriva — ma un ramo `preview` che degrada in silenzio in un
            // invio reale e' la trappola che questo hook esiste per chiudere.
            if (!draft) {
              throw new Error(
                "Anteprima della mail di ritiro non disponibile: invio annullato per sicurezza.",
              );
            }
            setPickupComposeError(null);
            setPickupComposeState({
              orderId: order.id,
              draft,
              original: { ...draft },
              errors: preview.errors ?? [],
            });
            keepBusy = true;
            return;
          }
          // Difensivo: il backend non ha rispettato preview:true e ha già
          // inviato. Trattiamo come invio avvenuto (stesso feedback di successo).
          onMessage(formatPickupMailMessage(preview, backend));
          if (!isMockMode) void onRefresh();
          return;
        }
        const result = await sendPickupMail(order.id);
        onMessage(formatPickupMailMessage(result, backend));
        // Refresh IMMEDIATO (Davide 2026-07-06 «devi segnare che l'ho
        // inviata»): il backend marca pickup_mail_app_sent_at nel raw al
        // send, ma senza rifetch la card restava col raw vecchio e il badge
        // "LDV: in attesa conferma" compariva solo ricaricando la pagina.
        if (!isMockMode) void onRefresh();
      } catch (err) {
        onError(prettifyApiError(err));
      } finally {
        // Se abbiamo aperto il popup, il busy id resta finche' l'invio va a
        // buon fine (handlePickupComposeSend) o si chiude il popup (onClose).
        if (!keepBusy) releaseBusy(order.id);
      }
    },
    [onMessage, onError, onRefresh, isMockMode, releaseBusy],
  );

  /** Conferma dal popup pre-invio pickup (Task 11): manda l'override del
   *  delta edited↔original. Successo → chiude il popup + STESSO feedback
   *  (``pickupMailFeedback``) del ramo diretto; errore → resta nel dialog. */
  async function handlePickupComposeSend(edited: MailDraft[]) {
    if (!pickupComposeState) return;
    const editedDraft = edited[0];
    if (!editedDraft) return;
    setPickupComposeSending(true);
    setPickupComposeError(null);
    try {
      const result = await sendPickupMail(pickupComposeState.orderId, {
        mailOverride: draftToOverride(editedDraft, pickupComposeState.original),
      });
      // Il popup esiste SOLO sul ramo smtp: qui la mail e' appena partita dal
      // server, non c'e' nessun Thunderbird da andare a cliccare.
      onMessage(formatPickupMailMessage(result, "smtp"));
      // Stesso refresh immediato del ramo diretto: il badge pickup deve
      // comparire appena la mail parte, non al reload successivo.
      if (!isMockMode) void onRefresh();
      setPickupComposeState(null);
      // Il busy si rilascia SOLO qui (invio riuscito) o in ``onClose``: sul ramo
      // d'errore il popup resta aperto, e rilasciarlo nel ``finally`` riaccendeva
      // il bottone «Invia LDV» col dialog ancora a schermo — feedback che mente,
      // e la porta a un secondo popup per un altro ordine.
      releaseBusy(pickupComposeState.orderId);
    } catch (err) {
      setPickupComposeError(prettifyApiError(err));
    } finally {
      setPickupComposeSending(false);
    }
  }

  const dialog = pickupComposeState && (
    /* Popup pre-invio pickup mail (Task 11): gatato su mail_backend ===
       "smtp" in handleSendPickupMail. 1 bozza sola per ordine. */
    <MailComposeMultiDialog
      // ``MailComposeMultiDialog`` inizializza ``edited`` solo al mount (lazy
      // initializer, nessuna risincronizzazione sulla prop ``drafts``): senza
      // key, cambiare ordine a dialog montato mostrerebbe la bozza VECCHIA
      // sotto il nuovo orderId → LDV sbagliata al ritiro sbagliato.
      key={pickupComposeState.orderId}
      drafts={[pickupComposeState.draft]}
      title="Conferma invio mail di spedizione"
      onSendAll={(edited) => handlePickupComposeSend(edited)}
      onClose={() => {
        if (!pickupComposeSending) {
          // Chiusura senza invio: rilascia il busy id tenuto aperto dal
          // ramo smtp (keepBusy), altrimenti il bottone pickup resterebbe
          // disabilitato fino al refresh.
          const closedOrderId = pickupComposeState.orderId;
          setPickupComposeState(null);
          releaseBusy(closedOrderId);
        }
      }}
      sending={pickupComposeSending}
      sendError={pickupComposeError}
      previewErrors={pickupComposeState.errors}
    />
  );

  return { sendPickupMail: handleSendPickupMail, pickupMailBusyIds, dialog };
}
